// hero.ts — hero section animation, ported from the design attachment's anim-p1.js
// (lines 4292-4470): "one-way latency transformation". It is one-way by design — high
// (bad, red) ping settles to low (good, green) and stays there, matching the original.
//
// The static markup this module enhances lives in `../sections/hero.tsx` and mirrors the
// design's own no-JS state (see that file's header comment). This module does exactly what
// anim-p1.js did to that markup: it selects elements via `data-anim`, rewrites the ping
// number and chart SVG into their instrumented HUD form, and drives them every frame from
// `clock` instead of `NP.register`'s per-timeline `trigger`/`duration` bookkeeping — that
// bookkeeping is redundant here because `useSectionAnimation` already gates the whole
// module behind one IntersectionObserver-triggered mount (engine.ts's contract: `clock`
// only ever runs once the section is in view, and under reduced motion it calls this
// module's subscriber exactly once with `elapsedMs = Infinity` so the resting frame draws
// deterministically instead of an empty t=0 one).
//
// `outExpoSettle`/`clamp01`/`enter` are re-implemented locally rather than imported: the
// shared engine (engine.ts) deliberately ports only the clock, not np-engine.js's ease/
// builder helpers (see that file's header) — each SectionModule owns whatever timeline
// shape it needs on top of clock.subscribe().
import type { SectionModule } from './use-section-animation';

const ENTRY_DURATION_MS = 750;
const ENTRY_STAGGER_MS = 110;
const ENTRY_RISE_PX = 20;
const ENTRY_BLUR_PX = 8;

const PILL_ENTRY_OFFSET_MS = 720;
const PILL_STAGGER_MS = 60;
const PILL_RISE_PX = 18;
const PILL_BLUR_PX = 8;

const OPTIMIZATION_START_MS = 900;
const OPTIMIZATION_SPAN_MS = 5000;
const ROUTE_REVEAL_START_MS = 1150;
const ROUTE_REVEAL_SPAN_MS = 2600;
const RED_GREEN_SPLIT_INDEX = 8;
const GOOD_THRESHOLD_MS = 64;
const GOOD_RANGE_MS = 48;
const PHASE_TRANSITION_2_THRESHOLD = 0.52;
const PHASE_TRANSITION_3_THRESHOLD = 0.88;

const GLOW_PERIOD_MS = 6500;
const LIVE_DOT_PERIOD_MS = 1625;

const CHART_VALUES_MS = [168, 174, 159, 192, 151, 136, 111, 87, 64, 43, 29, 19, 13, 10, 8];
const CHART_X_START = 42;
const CHART_X_SPAN = 716;
const CHART_Y_BASELINE = 350;
const CHART_Y_SCALE = 205;
const CHART_Y_RANGE_PX = 258;

const RED_COLOR: [number, number, number] = [253, 49, 84];
const GREEN_COLOR: [number, number, number] = [147, 255, 24];

interface ChartPoint {
  x: number;
  y: number;
  v: number;
}

interface SampledPoint {
  x: number;
  y: number;
  v: number;
  index: number;
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function outExpoSettle(value: number): number {
  return value >= 1 ? 1 : 1 - 2 ** (-10 * value);
}

interface EnterOptions {
  durationMs: number;
  staggerMs: number;
  risePx: number;
  blurPx: number;
}

function enter(elements: Array<HTMLElement | null>, elapsedMs: number, { durationMs, staggerMs, risePx, blurPx }: EnterOptions): void {
  elements.forEach((element, index) => {
    if (!element) return;
    const node = element;
    const progress = clamp01((elapsedMs - index * staggerMs) / durationMs);
    const eased = outExpoSettle(progress);
    node.style.opacity = String(eased);
    node.style.transform = progress >= 1 ? 'none' : `translateY(${((1 - eased) * risePx).toFixed(2)}px)`;
    node.style.filter = progress >= 1 ? 'none' : `blur(${((1 - eased) * blurPx).toFixed(2)}px)`;
  });
}

function buildChartPoints(): ChartPoint[] {
  return CHART_VALUES_MS.map((value, index) => ({
    x: CHART_X_START + index * (CHART_X_SPAN / (CHART_VALUES_MS.length - 1)),
    y: CHART_Y_BASELINE - (value / CHART_Y_SCALE) * CHART_Y_RANGE_PX,
    v: value,
  }));
}

function formatPath(points: ChartPoint[]): string {
  const first = points[0];
  if (!first) return '';
  let path = `M${first.x.toFixed(1)},${first.y.toFixed(1)}`;
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (point) path += ` L${point.x.toFixed(1)},${point.y.toFixed(1)}`;
  }
  return path;
}

function sampleChart(points: ChartPoint[], progress: number): SampledPoint {
  const clampedIndex = Math.max(0, Math.min(points.length - 1, progress * (points.length - 1)));
  const lowerIndex = Math.min(points.length - 2, Math.floor(clampedIndex));
  const fraction = clampedIndex - lowerIndex;
  const lower = points[lowerIndex];
  const upper = points[lowerIndex + 1];
  if (!lower || !upper) return { x: 0, y: 0, v: 0, index: lowerIndex };
  return {
    x: lower.x + (upper.x - lower.x) * fraction,
    y: lower.y + (upper.y - lower.y) * fraction,
    v: lower.v + (upper.v - lower.v) * fraction,
    index: lowerIndex,
  };
}

function partialChartPoints(points: ChartPoint[], progress: number): { drawn: ChartPoint[]; current: SampledPoint } {
  const current = sampleChart(points, progress);
  const drawn = points.slice(0, current.index + 1);
  drawn.push({ x: current.x, y: current.y, v: current.v });
  return { drawn, current };
}

function rgbMix(from: [number, number, number], to: [number, number, number], mix: number): string {
  const mixChannel = (start: number, end: number) => Math.round(start + (end - start) * mix);
  return `rgb(${mixChannel(from[0], to[0])},${mixChannel(from[1], to[1])},${mixChannel(from[2], to[2])})`;
}

const CHART_SVG_MARKUP =
  '<defs>' +
  '<linearGradient id="np-h-bg" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#151018"/><stop offset=".58" stop-color="#0b0e10"/><stop offset="1" stop-color="#081008"/></linearGradient>' +
  '<linearGradient id="np-h-good-area" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#93FF18" stop-opacity=".25"/><stop offset="1" stop-color="#93FF18" stop-opacity="0"/></linearGradient>' +
  '<filter id="np-h-red-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>' +
  '<filter id="np-h-green-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>' +
  '<clipPath id="np-h-plot"><rect x="42" y="62" width="716" height="300" rx="8"/></clipPath>' +
  '</defs>' +
  '<rect width="800" height="450" fill="url(#np-h-bg)"/>' +
  '<g opacity=".42" stroke="#fff" stroke-opacity=".08" stroke-dasharray="3 8">' +
  '<path d="M42 92H758M42 182H758M42 272H758M42 362H758"/><path d="M42 62V362M185 62V362M328 62V362M471 62V362M614 62V362M758 62V362"/>' +
  '</g>' +
  '<g class="np-h-axis" font-family="monospace" font-size="10" letter-spacing="1.2"><text class="np-h-axis-title" x="42" y="31" fill="#fff" fill-opacity=".78"></text><text class="np-h-axis-engine" x="758" y="31" text-anchor="end" fill="#93FF18"></text><text class="np-h-axis-before" x="42" y="410" fill="#FD3154"></text><text class="np-h-axis-after" x="758" y="410" text-anchor="end" fill="#93FF18"></text></g>' +
  '<g clip-path="url(#np-h-plot)">' +
  '<path class="np-h-area" fill="url(#np-h-good-area)" d=""/>' +
  '<path class="np-h-bad" fill="none" stroke="#FD3154" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" filter="url(#np-h-red-glow)" d=""/>' +
  '<path class="np-h-good" fill="none" stroke="#93FF18" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" filter="url(#np-h-green-glow)" d=""/>' +
  '<line class="np-h-scan" y1="62" y2="362" stroke="#fff" stroke-opacity=".28" stroke-dasharray="2 5"/>' +
  '<rect class="np-h-scan-fill" y="62" width="72" height="300" fill="#93FF18" fill-opacity=".035"/>' +
  '</g>' +
  '<g class="np-h-cursor"><circle r="15" fill="#FD3154" fill-opacity=".13"/><circle r="5" fill="#FD3154"/></g>' +
  '<g class="np-h-callout"><rect width="126" height="32" rx="8" fill="#11151a" stroke="#FD3154" stroke-opacity=".55"/><text x="63" y="20.5" text-anchor="middle" fill="#FD3154" font-family="monospace" font-size="9" font-weight="700">SCANNING ROUTES</text></g>' +
  '<g class="np-h-route-chips" transform="translate(42 423)" font-family="monospace" font-size="8"></g>';

function buildRouteChipsMarkup(): string {
  let chipHtml = '';
  for (let chipIndex = 0; chipIndex < 5; chipIndex += 1) {
    chipHtml += `<g transform="translate(${chipIndex * 94} 0)"><rect width="84" height="18" rx="9" fill="#fff" fill-opacity=".035" stroke="#fff" stroke-opacity=".08"/><circle cx="10" cy="9" r="2.5" fill="#FD3154"/><text x="18" y="12" fill="#929298">ROUTE ${chipIndex + 1}</text></g>`;
  }
  return chipHtml;
}

export const heroAnimation: SectionModule = ({ root, strings, clock }) => {
  const kicker = root.querySelector<HTMLElement>('[data-anim="hero-kicker"]');
  const heading = root.querySelector<HTMLElement>('[data-anim="hero-h1"]');
  const subheading = root.querySelector<HTMLElement>('[data-anim="hero-sub"]');
  const pingRoot = root.querySelector<HTMLElement>('[data-anim="hero-ping-number"]');
  const chartSvg = root.querySelector<SVGSVGElement>('[data-anim="hero-chart"]');
  const formCard = root.querySelector<HTMLElement>('[data-anim="hero-form-card"]');
  const pillsContainer = root.querySelector<HTMLElement>('[data-anim="hero-stat-pills"]');

  if (!kicker || !heading || !subheading || !pingRoot || !chartSvg || !formCard || !pillsContainer) return () => {};

  const chartBox = chartSvg.parentElement;
  chartBox?.classList.add('np-chart-box', 'np-hero-console');

  const mainEls: Array<HTMLElement | null> = [kicker, heading, subheading, pingRoot, chartBox, formCard];
  const pillEls = Array.from(pillsContainer.children) as HTMLElement[];

  // --- odometer-style ping number (bar.md §7 in the source's own terms) ------------------
  const numDiv = pingRoot.firstElementChild as HTMLElement | null;
  const label = pingRoot.lastElementChild as HTMLElement | null;
  if (!numDiv || !label) return () => {};

  numDiv.innerHTML = '<span class="np-hero-main-number">168</span><span class="np-hero-unit">ms</span>';
  const pingNum = numDiv.querySelector<HTMLElement>('.np-hero-main-number');

  const phase = document.createElement('span');
  phase.className = 'np-hero-phase';
  phase.innerHTML = '<i></i><b></b><em></em>';
  pingRoot.appendChild(phase);
  const phaseDot = phase.querySelector<HTMLElement>('i');
  const phaseText = phase.querySelector<HTMLElement>('b');
  const phaseMeta = phase.querySelector<HTMLElement>('em');
  if (!pingNum || !phaseDot || !phaseText || !phaseMeta) return () => {};

  // --- chart: rebuild into its instrumented HUD form -------------------------------------
  chartSvg.innerHTML = CHART_SVG_MARKUP;
  // The four chart labels are written from the strings rather than baked into the markup, so a
  // locale can translate them (SCRUM-1882). The status strip above the chart is deliberately not
  // among them: `ROUTE LOCKED` shares that element with `8 ms / stable` and reads as the
  // instrument's readout, the same call the Multi Internet telemetry follows.
  const axisTitle = chartSvg.querySelector<SVGTextElement>('.np-h-axis-title');
  const axisEngine = chartSvg.querySelector<SVGTextElement>('.np-h-axis-engine');
  const axisBefore = chartSvg.querySelector<SVGTextElement>('.np-h-axis-before');
  const axisAfter = chartSvg.querySelector<SVGTextElement>('.np-h-axis-after');
  if (axisTitle) axisTitle.textContent = strings.hero.chartTitle;
  if (axisEngine) axisEngine.textContent = strings.hero.chartEngine;
  if (axisBefore) axisBefore.textContent = strings.hero.chartBefore;
  if (axisAfter) axisAfter.textContent = strings.hero.chartAfter;
  const routeChips = chartSvg.querySelector<SVGGElement>('.np-h-route-chips');
  if (routeChips) routeChips.innerHTML = buildRouteChipsMarkup();

  const badPath = chartSvg.querySelector<SVGPathElement>('.np-h-bad');
  const goodPath = chartSvg.querySelector<SVGPathElement>('.np-h-good');
  const areaPath = chartSvg.querySelector<SVGPathElement>('.np-h-area');
  const cursor = chartSvg.querySelector<SVGGElement>('.np-h-cursor');
  const scan = chartSvg.querySelector<SVGLineElement>('.np-h-scan');
  const scanFill = chartSvg.querySelector<SVGRectElement>('.np-h-scan-fill');
  const callout = chartSvg.querySelector<SVGGElement>('.np-h-callout');
  if (!badPath || !goodPath || !areaPath || !cursor || !scan || !scanFill || !callout || !routeChips) return () => {};

  const cursorHalo = cursor.children[0] as SVGElement | undefined;
  const cursorCore = cursor.children[1] as SVGElement | undefined;
  const callRect = callout.querySelector<SVGRectElement>('rect');
  const callText = callout.querySelector<SVGTextElement>('text');
  if (!cursorHalo || !cursorCore || !callRect || !callText) return () => {};

  const chipGroups = Array.from(routeChips.children) as SVGGElement[];
  const chartPoints = buildChartPoints();

  // Nested closures don't inherit the null-narrowing from the guards above (TS widens back to
  // each querySelector's declared, nullable return type inside a function declared later in the
  // same scope) — so the chart/HUD nodes are re-bound here, once, into objects whose properties
  // TS infers as non-null from this exact assignment.
  const chart = { badPath, goodPath, areaPath, cursor, cursorHalo, cursorCore, scan, scanFill, callout, callRect, callText };
  const hud = { pingNum, numDiv, phaseDot, phaseText, phaseMeta };

  function applyOptimization(elapsedMs: number): void {
    const clampedElapsed = Math.max(0, elapsedMs);
    const progress = clamp01((clampedElapsed - OPTIMIZATION_START_MS) / OPTIMIZATION_SPAN_MS);
    const { drawn, current } = partialChartPoints(chartPoints, progress);

    const redPoints = drawn.slice(0, Math.min(drawn.length, RED_GREEN_SPLIT_INDEX + 1));
    const greenPoints = drawn.length > RED_GREEN_SPLIT_INDEX ? chartPoints.slice(RED_GREEN_SPLIT_INDEX, Math.min(chartPoints.length, current.index + 1)) : [];
    if (drawn.length > RED_GREEN_SPLIT_INDEX) greenPoints.push({ x: current.x, y: current.y, v: current.v });

    chart.badPath.setAttribute('d', formatPath(redPoints));
    chart.goodPath.setAttribute('d', formatPath(greenPoints));
    const firstGreen = greenPoints[0];
    chart.areaPath.setAttribute('d', firstGreen ? `${formatPath(greenPoints)} L${current.x.toFixed(1)},362 L${firstGreen.x.toFixed(1)},362 Z` : '');

    const goodness = clamp01((GOOD_THRESHOLD_MS - current.v) / GOOD_RANGE_MS);
    const color = rgbMix(RED_COLOR, GREEN_COLOR, goodness);
    const display = Math.round(current.v);

    hud.pingNum.textContent = String(display);
    hud.numDiv.style.color = color;
    chart.cursor.setAttribute('transform', `translate(${current.x.toFixed(1)},${current.y.toFixed(1)})`);
    chart.cursorHalo.setAttribute('fill', color);
    chart.cursorCore.setAttribute('fill', color);
    chart.scan.setAttribute('x1', current.x.toFixed(1));
    chart.scan.setAttribute('x2', current.x.toFixed(1));
    chart.scanFill.setAttribute('x', (current.x - 70).toFixed(1));

    const calloutX = Math.max(44, Math.min(630, current.x - 63));
    const calloutY = Math.max(68, current.y - 48);
    chart.callout.setAttribute('transform', `translate(${calloutX.toFixed(1)},${calloutY.toFixed(1)})`);
    chart.callRect.setAttribute('stroke', color);
    chart.callText.setAttribute('fill', color);

    const activeRoutes = Math.min(5, Math.floor(clamp01((clampedElapsed - ROUTE_REVEAL_START_MS) / ROUTE_REVEAL_SPAN_MS) * 6));
    chipGroups.forEach((group, index) => {
      const isActive = index < activeRoutes;
      const isWinner = index === 3 && progress > 0.55;
      let activeColor = '#FD3154';
      if (isWinner) activeColor = '#93FF18';
      group.querySelector('circle')?.setAttribute('fill', isActive ? activeColor : '#424A5B');
      const rect = group.querySelector('rect');
      rect?.setAttribute('stroke', isActive ? activeColor : '#FFFFFF');
      rect?.setAttribute('stroke-opacity', isActive ? '.45' : '.08');
    });

    if (clampedElapsed < OPTIMIZATION_START_MS) {
      hud.phaseText.textContent = strings.hero.baselineDetected;
      hud.phaseMeta.textContent = '168 ms / unstable';
      chart.callText.textContent = strings.hero.baselineCapture;
    } else if (progress < PHASE_TRANSITION_2_THRESHOLD) {
      hud.phaseText.textContent = strings.hero.testing5Routes;
      hud.phaseMeta.textContent = `${activeRoutes} / 5 routes`;
      chart.callText.textContent = strings.hero.scanningRoutes;
    } else if (progress < PHASE_TRANSITION_3_THRESHOLD) {
      hud.phaseText.textContent = strings.hero.smartReroute;
      hud.phaseMeta.textContent = 'route 4 selected';
      chart.callText.textContent = strings.hero.reroutingPackets;
    } else {
      hud.phaseText.textContent = strings.hero.routeLocked;
      hud.phaseMeta.textContent = `${display} ms / stable`;
      chart.callText.textContent = strings.hero.optimalRoute;
    }
    hud.phaseDot.style.background = color;
    hud.phaseDot.style.boxShadow = `0 0 10px ${color}`;
    hud.phaseText.style.color = color;
  }

  // --- card breathing glow -----------------------------------------------------------------
  const glowEl = document.createElement('div');
  glowEl.className = 'np-card-glow';
  glowEl.setAttribute('aria-hidden', 'true');
  formCard.insertBefore(glowEl, formCard.firstChild);

  function applyGlow(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs)) {
      glowEl.style.opacity = '0';
      return;
    }
    const wave = 0.5 - 0.5 * Math.cos((Math.PI * 4 * elapsedMs) / GLOW_PERIOD_MS);
    glowEl.style.opacity = (0.16 + 0.46 * wave).toFixed(3);
  }

  // --- live-ping kicker dot: engine-driven so it closes deterministically ------------------
  const liveDot = kicker.querySelector<HTMLElement>('.animate-pulse');

  function applyLiveDot(elapsedMs: number): void {
    if (!liveDot) return;
    if (!Number.isFinite(elapsedMs)) {
      liveDot.style.opacity = '1.000';
      return;
    }
    const wave = 0.5 - 0.5 * Math.cos((2 * Math.PI * elapsedMs) / LIVE_DOT_PERIOD_MS);
    liveDot.style.opacity = (1 - 0.5 * wave).toFixed(3);
  }

  // The source calls these two once, synchronously, right after wiring the timelines
  // (`applyOptimization(0, NP.api); applyGlow(0, NP.api);` at the bottom of anim-p1.js) so
  // the chart and card paint their t=0 frame immediately instead of waiting for the first
  // rAF tick. `enter()` is deliberately NOT force-invoked here — the reveal's hidden t=0
  // frame (driven by the CSS pre-reveal rule) is the intended starting state.
  applyOptimization(0);
  applyGlow(0);

  const unsubscribe = clock.subscribe((elapsedMs) => {
    enter(mainEls, elapsedMs, { durationMs: ENTRY_DURATION_MS, staggerMs: ENTRY_STAGGER_MS, risePx: ENTRY_RISE_PX, blurPx: ENTRY_BLUR_PX });
    enter(pillEls, elapsedMs - PILL_ENTRY_OFFSET_MS, { durationMs: ENTRY_DURATION_MS, staggerMs: PILL_STAGGER_MS, risePx: PILL_RISE_PX, blurPx: PILL_BLUR_PX });
    applyOptimization(elapsedMs);
    applyGlow(elapsedMs);
    applyLiveDot(elapsedMs);
  });

  return () => {
    unsubscribe();
  };
};
